/* ############################################################################
 *  RESPONSIVE TOOLTIP
 *  This is a combo component that combines Tooltip and SimplePopover components
 *  It renders Tooltip on desktop and SimplePopover on mobile
 * ######################################################################### */

import {
  InfoBubbleVariant,
  PopoverTrigger,
  SimplePopover,
  SimplePopoverContent,
  SimplePopoverHeader,
  SimplePopoverText,
  Tooltip,
  TooltipContent,
  TooltipHeader,
  TooltipText,
  TooltipTrigger,
  supportsHover as supportsHoverHelper,
} from '@bridebook/aura';
import { ComponentProps } from 'react';

type ResponsiveTooltipProps = {
  header?: string;
  text: string;
  /**
   *
   * If not provided, it checks if the device supports hover events and renders Tooltip or SimplePopover accordingly.
   * Can be overridden in some edge cases like for example showcasing it in Storybook.
   */
  supportsHover?: boolean;
  variant?: InfoBubbleVariant;
  className?: string;
  children: React.ReactNode;
  /**
   * The side of the tooltip/popover.
   * If not provided, it defaults to 'bottom'. It automatically adjusts if is rendered close to the edge of the screen.
   */
  side?: ComponentProps<typeof TooltipContent>['side'];
  sideOffset?: ComponentProps<typeof TooltipContent>['sideOffset'];
  classNames?: {
    trigger?: string;
  };
} & ComponentProps<typeof TooltipTrigger>;

export const ResponsiveTooltip = ({
  supportsHover: supportsHoverProp,
  variant = 'dark',
  header,
  text,
  children,
  className,
  side = 'top',
  sideOffset = 0,
  classNames,
  ...props
}: ResponsiveTooltipProps) => {
  const supportsHoverLocal = supportsHoverHelper();
  const supportsHover = supportsHoverProp ?? supportsHoverLocal;

  if (supportsHover) {
    return (
      <Tooltip variant={variant}>
        <TooltipTrigger className={classNames?.trigger} {...props}>
          {children}
        </TooltipTrigger>
        <TooltipContent className={className} side={side} sideOffset={sideOffset}>
          {header && <TooltipHeader>{header}</TooltipHeader>}
          {text && <TooltipText>{text}</TooltipText>}
        </TooltipContent>
      </Tooltip>
    );
  }

  // on MOBILE render SimplePopover
  return (
    <SimplePopover variant={variant} closeOnScroll>
      <PopoverTrigger className={classNames?.trigger} {...props}>
        {children}
      </PopoverTrigger>
      <SimplePopoverContent className={className} side={side} sideOffset={sideOffset}>
        {header && <SimplePopoverHeader>{header}</SimplePopoverHeader>}
        {text && <SimplePopoverText>{text}</SimplePopoverText>}
      </SimplePopoverContent>
    </SimplePopover>
  );
};
